import React, { useState } from "react";
import { api, formatApiError, fmtINR, API } from "../lib/apiClient";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";
import { DownloadSimple, ChartLineUp, FileXls } from "@phosphor-icons/react";

function firstDayOfMonth(d = new Date()) { return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); }
function lastDayOfMonth(d = new Date()) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10); }

export default function Gstr1ReportPage() {
  const [from, setFrom] = useState(firstDayOfMonth());
  const [to, setTo] = useState(lastDayOfMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/gstr1", { params: { from, to } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const download = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `gstr1-${from}-to-${to}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("GSTR-1 JSON downloaded");
  };

  const downloadXlsx = async () => {
    try {
      const res = await fetch(`${API}/reports/gstr1.xlsx?from=${from}&to=${to}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `gstr1-${from}-to-${to}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("GSTR-1 Excel downloaded");
    } catch { toast.error("Download failed"); }
  };

  return (
    <div data-testid="gstr1-page">
      <PageHeader
        title="GSTR-1 Report"
        subtitle="Generate the monthly outward supplies summary. Download JSON for upload to GST portal / reconciliation."
      />

      <div className="p-8 max-w-5xl space-y-6">
        <div className="card-base p-5 flex items-end gap-4 flex-wrap">
          <div>
            <div className="label-overline block mb-1.5">From</div>
            <input type="date" className="field" value={from} onChange={e => setFrom(e.target.value)} data-testid="gstr-from" />
          </div>
          <div>
            <div className="label-overline block mb-1.5">To</div>
            <input type="date" className="field" value={to} onChange={e => setTo(e.target.value)} data-testid="gstr-to" />
          </div>
          <button onClick={generate} disabled={loading} className="btn-primary inline-flex items-center gap-1.5" data-testid="gstr-generate">
            <ChartLineUp size={14} weight="bold" /> {loading ? "Generating…" : "Generate Report"}
          </button>
          {data && <>
            <button onClick={download} className="btn-secondary inline-flex items-center gap-1.5" data-testid="gstr-download"><DownloadSimple size={14} weight="bold" /> Download JSON</button>
            <button onClick={downloadXlsx} className="btn-secondary inline-flex items-center gap-1.5" data-testid="gstr-download-xlsx"><FileXls size={14} weight="bold" /> Excel</button>
          </>}
        </div>

        {data && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <KpiLite label="Period" value={`${data.period.from} → ${data.period.to}`} mono />
              <KpiLite label="Taxable Value" value={fmtINR(data.totals.taxable)} />
              <KpiLite label="CGST" value={fmtINR(data.totals.cgst)} />
              <KpiLite label="SGST" value={fmtINR(data.totals.sgst)} />
              <KpiLite label="IGST" value={fmtINR(data.totals.igst)} />
            </div>

            <Section title={`B2B (Registered customers) — ${data.b2b.length}`} empty="No B2B invoices in this period.">
              {data.b2b.map((grp) => (
                <div key={grp.ctin} className="mb-3">
                  <div className="font-mono text-xs text-ink-muted mb-1">GSTIN: {grp.ctin}</div>
                  {grp.inv.map(inv => <InvRow key={inv.inum} inv={inv} />)}
                </div>
              ))}
            </Section>

            <Section title={`B2CS (Unregistered / URP) — ${data.b2cs.length}`} empty="No B2C invoices in this period.">
              {data.b2cs.map(inv => <InvRow key={inv.inum} inv={inv} />)}
            </Section>

            <Section title={`CDNR (Credit/Debit Notes – Registered) — ${data.cdnr.length}`} empty="No credit/debit notes for registered parties in this period.">
              {data.cdnr.map(inv => <InvRow key={inv.inum} inv={inv} />)}
            </Section>
          </>
        )}
      </div>

      <FieldStyle />
    </div>
  );
}

function KpiLite({ label, value, mono }) {
  return (
    <div className="card-base p-4">
      <div className="label-overline mb-1">{label}</div>
      <div className={`font-semibold text-ink ${mono ? "font-mono text-sm" : "text-base tabular"}`}>{value}</div>
    </div>
  );
}

function Section({ title, empty, children }) {
  const isEmpty = !children || (Array.isArray(children) && children.length === 0);
  return (
    <div className="card-base p-5">
      <div className="font-display font-semibold mb-3">{title}</div>
      {isEmpty ? <div className="text-xs text-ink-muted">{empty}</div> : <div>{children}</div>}
    </div>
  );
}

function InvRow({ inv }) {
  return (
    <div className="py-2 px-3 rounded-md bg-surface-muted/50 flex items-center justify-between text-xs mb-1.5">
      <div className="flex-1"><span className="font-mono font-semibold">{inv.inum}</span> · {inv.idt}{inv.pname ? <> · <span className="font-semibold">{inv.pname}</span></> : null} · POS {inv.pos || "—"} {inv.ntty && <span className="ml-1 status-pill status-info">{inv.ntty === "C" ? "Credit" : "Debit"} Note</span>}</div>
      <div className="tabular font-semibold">{fmtINR(inv.val)}</div>
    </div>
  );
}
